// Which gallery the yearbook pose picker is choosing a pose for.
import { createSelector } from 'reselect';
import type { YearbookPickerGallery } from '../actions/yearbook';

// The part of buildGalleryOrderContext's galleryThatInitiatedOrder read here.
interface InitiatingGallery {
  galleryId?: number | string | null;
  studentId?: number | string | null;
}

export interface YearbookPickerState {
  additionalData?: {
    picsGalleryId?: number | string | null;
    // The opened gallery's key (its password), for either gallery kind.
    picsGalleryPassword?: string | null;
    galleryThatInitiatedOrder?: InitiatingGallery | null;
  } | null;
}

// The DP2 gallery the parent opened the picker from, by key and id, for the
// picker's requests to name (ENG-3574). The route carries only the student,
// so a student with DP2 galleries from two school years would otherwise get
// whichever gallery the server picks for the student. The server looks the
// gallery up by its key and lets the id only choose among the galleries that
// key matches, so the two travel together or not at all.
//
// Only a DP2 order context (no picsGalleryId) opened for this same student,
// with a positive integer gallery id and a non-blank key, counts. Anything
// else (a Pics gallery, a context left from another student, a store
// persisted before the context carried a student or a key) is null, and the
// server chooses.
//
// The inputs are the context's primitives, not its objects, so the result
// keeps its reference until one of those values changes.
export const selectYearbookPickerDp2Gallery = createSelector(
  [
    (state: YearbookPickerState) => state.additionalData?.picsGalleryId,
    (state: YearbookPickerState) =>
      state.additionalData?.galleryThatInitiatedOrder?.galleryId,
    (state: YearbookPickerState) =>
      state.additionalData?.galleryThatInitiatedOrder?.studentId,
    (state: YearbookPickerState) => state.additionalData?.picsGalleryPassword,
    (_state: YearbookPickerState, routeStudentId: string) => routeStudentId,
  ],
  (
    picsGalleryId,
    galleryId,
    studentId,
    galleryKey,
    routeStudentId,
  ): YearbookPickerGallery | null => {
    if (picsGalleryId != null) return null;
    if (
      typeof galleryId !== 'number' ||
      !Number.isInteger(galleryId) ||
      galleryId <= 0
    ) {
      return null;
    }
    if (studentId == null || String(studentId) !== String(routeStudentId)) {
      return null;
    }
    if (typeof galleryKey !== 'string' || galleryKey.trim() === '') {
      return null;
    }

    return { id: galleryId, key: galleryKey };
  },
);
